import { mean, variance } from 'mdatools/stat';
import { pnorm } from 'mdatools/distributions';

import { pcares_get, pcamodel_get } from '@mdatools-apps/wasm/pca.js';
import { seq, replacer, convertToIntervals, checkedResultOutliers } from '../utils.js';

/**
 * Assess a particular value of full distance.
 *
 * @param {number} f - distance value.
 * @param {number} eCrit - critical limit for extremes.
 * @param {number} oCrit - critical limit for outliers.
 *
 * @returns {Array} array with role ('regular', 'extreme' or 'outlier') and decision ('in' or 'out')
 */
function assess(f, eCrit, oCrit) {

   if (f < eCrit) {
      return ['regular', 'in'];
   }

   if (f > oCrit) {
      return ['outlier', 'out'];
   }

   return ['extreme', 'out'];
}


/**
 * Make SIMCA predictions for a particular component considering only members.
 *
 * @param {number} f - vector with full distance values.
 * @param {number} eCrit - critical limit for extremes.
 * @param {number} oCrit - critical limit for outliers.
 * @param {Array} rols - array with roles initiated in caller (will be mutated inside).
 * @param {Array} decs - array with decisions initiated in caller (will be mutated inside).
 * @param {Object} rolsNum - JSON with number of items given a particular role, initiated in caller (will be mutated inside).
 * @param {Object} decsNum - JSON with number of items given a particular decision, initiated in caller (will be mutated inside).
 * @param {Array} ind - array with indices corresponding to class members.
 *
 * @returns {Array} array with number of true positives and false negatives.
 *
 * The vector f contains all distance values from the dataset (except outliers), while vector ind
 * contains positions in f corresponding to the members of the target class. If `ind` is undefined,
 * null or empty, method will return [0, 0] without processing the values.
 */
export function processMembers(f, eCrit, oCrit, rols, decs, rolsNum, decsNum, ind) {

   if (!ind || ind.length === 0) return [0, 0];
   let TP = 0, FN = 0;
   for (let k of ind) {
      const fk = f[k];
      const [r, d]  = assess(fk, eCrit, oCrit);
      rolsNum[r] += 1;
      decsNum[d] += 1;
      rols[k] = r;
      decs[k] = d;
      TP = TP + (d === 'in');
      FN = FN + (d !== 'in');
   }

   return [TP, FN];
}


/**
 * Make SIMCA predictions for a particular component considering only strangers.
 *
 * @param {number} f - vector with full distance values.
 * @param {number} k - degrees of freedom of the full distance (Nf).
 * @param {number} eCrit - critical limit for extremes.
 * @param {number} oCrit - critical limit for outliers.
 * @param {Array} rols - array with roles initiated in caller (will be mutated inside).
 * @param {Array} decs - array with decisions initiated in caller (will be mutated inside).
 * @param {Object} rolsNum - JSON with number of items given a particular role, initiated in caller (will be mutated inside).
 * @param {Object} decsNum - JSON with number of items given a particular decision, initiated in caller (will be mutated inside).
 * @param {Array} ind - array with indices corresponding to class strangers.
 *
 * @returns {Array} [TN, FP, beta, s, f0, hz, Mz, Sz, k, m]: the numbers of true negatives and false
 * positives, beta, the non-centrality s, the scaling factor f0, the approximation's hz, Mz and Sz,
 * the DoF k and the mean m of the fitted distances.
 *
 * The vector f contains all distance values from the dataset (except outliers), while vector ind
 * contains positions in f corresponding to the strangers of the target class. If `ind` is undefined,
 * null or empty, the method returns ten zeros without processing the values.
 *
 * The distribution is fitted by trimming the largest distances away until the remaining ones are
 * no more dispersed than a noncentral chi-square with k DoF can be, and the trimmed samples are
 * the ones given the role "external". When the trimming can not reach that state — fewer than two
 * values would be left, or the ones left carry no dispersion at all — the fit does not exist:
 * every fitted parameter comes back NaN, no sample is called "external", and only the counts,
 * which are taken object by object against the class limits, stay valid.
 *
 * This method is also used to process items with unknown classes.
 *
 */
export function processStrangers(f, k, eCrit, oCrit, rols, decs, rolsNum, decsNum, ind) {

   if (!ind || ind.length === 0) return [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

   let TN = 0, FP = 0;
   for (let i of ind) {
      const fk = f[i];
      const [r, d] = assess(fk, eCrit, oCrit);
      decsNum[d] += 1;
      rols[i] = 'alien';         // by default we assess all strangers as aliens
      rolsNum['alien'] += 1;
      decs[i] = d;
      TN = TN + (d !== 'in');
      FP = FP + (d === 'in');
   }

   // Step 1. Sort all distances

   // make a copy of strangers indices as we are going to change it
   const indv = ind.slice();

   // sort the indices so the largest will be at the end
   indv.sort((a, b) => f[a] - f[b]);
   let I = indv.length;

   // sort the distance values as well and save into temporary variable
   const fp = indv.map(v => f[v]);

   // Step 2. Try to fit the non-central chi-square
   let Disc = -1;
   let n = 0;
   let m = 0;
   let d = 0;
   let M1 = 0;

   // the answer for a set the distribution can not be fitted to, used by every guard below. the
   // decisions are taken object by object against the class limits and do not involve the fit at
   // all, so TN and FP stay valid; everything the fit would have produced is NaN, because any
   // number returned here would be invented. nobody is called "external" either — see the commit
   // step after the approximation
   const unfitted = () => [TN, FP, NaN, NaN, NaN, NaN, NaN, NaN, k, m];

   // the fit needs a variance, and a variance needs at least two values. with a single stranger
   // 'variance' divides by zero and every parameter below silently becomes NaN, so the case is
   // rejected here instead
   // (the empty case already returned above, so here 'fp' holds exactly one value)
   if (fp.length < 2) {
      m = mean(fp);
      return unfitted();
   }

   // samples trimmed off during the search. they are marked "external" only once the whole fit has
   // come out finite: a partial trim is an intermediate state of the search and not a verdict about
   // the objects, and 'rolsNum' is shared with the member and unknown calls for the same component,
   // so a half-finished trim left behind would be counted as though it had succeeded
   const trimmed = [];

   while (Disc < 0) {
      if (n > 0) {
         // dropping another value would leave a single one, which has no variance. 'M1' can rise
         // rather than fall as the largest values go, because trimming moves the mean as well, so
         // this is reachable whenever a low lying object keeps the remainder too dispersed
         if (fp.length < 3) return unfitted();

         // sample with largest f does not fit, so it is trimmed off and the next biggest assessed
         trimmed.push(indv[I - 1]);
         I = I - 1;
         indv.pop();
         fp.pop();
      }

      // compute parameters for moments squared equation and
      // its discriminant
      m = mean(fp);
      d = variance(fp);
      M1 = d / (m * m);
      Disc = 4 - 2 * k * M1;
      n = n + 1;
   }

   // 'Disc >= 0' is the same condition as 's >= 0': at Disc = 0 the fit gives x = k and so s = 0,
   // the central case, and 2/k is the largest M1 any noncentral chi-square with k DoF can produce
   // (M1 falls monotonically as s grows). what it does not cover is M1 = 0, which passes as
   // Disc = 4 while meaning no dispersion at all — the limit s -> infinity, with no finite solution
   // on this branch. identical retained distances give exactly that, and all-zero ones give 0/0
   if (!(M1 > 0) || !Number.isFinite(M1) || !Number.isFinite(Disc) || !(m > 0) || !(k > 0)) {
      return unfitted();
   }

   // Step 3. Solve the moment equations. writing x = k + s, the mean and variance of the scaled
   // distribution give M1*x^2 - 4x + 2k = 0, whose positive root is the one below
   const x = (2 + Math.sqrt(Disc)) / M1;

   // scaling factor and noncentrality
   const s = (x - k);
   const f0 = m / x;


   // Step 4. Sankaran's approximation to the noncentral chi-square distribution function
   const z = eCrit / f0;
   const hz = 1 - 2 * (k + s) * (k + 3 * s) / (3 * (k + 2 * s)**2);
   const r = (hz - 1) * (1 - 3 * hz);
   const p = (k + 2 * s) / (k + s)**2;
   const Mz = 1 + hz * p * (hz - 1 - 0.5 * (2 - hz) * r * p);
   const Sz = hz * Math.sqrt(2 * p) * (1 + 0.5 * r * p);

   // Step 5. Given alpha, calculate beta
   const beta = pnorm((Math.pow(z / (k + s), hz) - Mz) / Sz);

   // none of these may reach the plots as a non-number: 'PlotAliens' raises 'Sz' and 'Mz' to the
   // power 1/hz and 'PlotSelectivity' divides by 'Sz'
   if (![x, s, f0, hz, Mz, Sz, beta].every(Number.isFinite) || !(f0 > 0) || !(Sz > 0)) {
      return unfitted();
   }

   // the fit held, so the samples trimmed to reach it are the ones that did not belong to it
   for (const i of trimmed) rols[i] = 'external';
   rolsNum.alien = rolsNum.alien - trimmed.length;
   rolsNum.external = rolsNum.external + trimmed.length;

   return [TN, FP, beta, s, f0, hz, Mz, Sz, k, m];
}


/**
 * Compute SIMCA predictions.
 *
 * @param {Object} pcares - JSON with predicted PCA results.
 * @param {string} limType - type limit name to use for classification ('classic' or 'robust').
 * @param {Array} eCrit - array with critical limits for extreme objects.
 * @param {Array} oCrit - array with critical limits for outliers.
 * @param {Object} ind - JSON with arrays of row indices: 'members' and 'strangers' of the target
 * class and, optional, 'unknown'; without it every row is unknown.
 *
 * @returns {Object} JSON with SIMCA outcomes typical for target class set.
 */
export function predict(pcares, limType, eCrit, oCrit, ind) {

   const ncomp = pcares.ncomp;
   const nrows = pcares.nrows;

   if (!ind) {
      // if no indices is provided assuming all are unknowns
      ind = {members: [], strangers: [], unknown: seq(0, nrows - 1)};
   }

   const nMembers = ind.members.length;
   const nStrangers = ind.strangers.length;
   // a caller's own 'unknown' list was ignored: its rows got no role and no decision
   const nUnknown = ind.unknown ? ind.unknown.length : 0;

   const res = new Array(ncomp);
   const sensitivity = new Array(ncomp);
   const specificity = new Array(ncomp);
   const selectivity = new Array(ncomp);
   const efficiency = new Array(ncomp);
   const accuracy = new Array(ncomp);
   const components = new Array(ncomp);

   const Nq = limType === 'robust' ? pcares.rNq : pcares.cNq;
   const Nh = limType === 'robust' ? pcares.rNh : pcares.cNh;

   for (let a = 0; a < ncomp; a++) {
      const Nf = Nh[a] + Nq[a];
      const fa = pcares_get(pcares, (limType === 'robust' ? 'fdistr' : 'fdistc'), a + 1);
      const decs = new Array(nrows);
      const rols = new Array(nrows);
      const rolsNum = {'regular': 0, 'extreme': 0, 'outlier': 0, 'alien': 0, 'external': 0};
      const decsNum = {'in': 0, 'out': 0};
      const [TP, FN] = processMembers(fa, eCrit[a], oCrit[a], rols, decs, rolsNum, decsNum, ind.members);
      const [TN, FP, beta, s, f0, hz, Mz, Sz, k, m] = processStrangers(fa, Nf, eCrit[a], oCrit[a], rols, decs, rolsNum, decsNum, ind.strangers);

      if (nUnknown > 0) {
         processStrangers(fa, Nf, eCrit[a], oCrit[a], rols, decs, rolsNum, decsNum, ind.unknown);
      }

      sensitivity[a] = TP > 0 ? TP / (TP + FN) : 0;
      specificity[a] = TN > 0 ? TN / (TN + FP) : 0;
      selectivity[a] = 1 - beta;
      efficiency[a] = Math.sqrt(sensitivity[a] * specificity[a]);
      // the guard belongs on the denominator. requiring both 'TP' and 'TN' to be positive reported
      // zero for a model which classified most objects correctly but happened to accept none of
      // the members, or none of the strangers
      const nTotal = TP + TN + FP + FN;
      accuracy[a] = nTotal > 0 ? (TP + TN) / nTotal : 0;
      components[a] = a + 1;
      res[a] = {
         f: fa, decs, rols, decsNum, rolsNum, TP, FN, TN, FP, beta, s, f0, hz, Mz, Sz, k, m
      }
   }

   return {
      class: ['simcares'],
      classes: pcares.classes,
      classNames: pcares.classNames,
      ind,
      nMembers,
      nStrangers,
      nUnknown,
      v: res,
      nrows: pcares.nrows,
      stat: {sensitivity, specificity, selectivity, efficiency, accuracy, components}
   }
}


// the apps sharing the two download helpers: the title and the link written to the results file;
// the file names start with the app id. the names were ddsimca's for both apps
const APPS = {
   'ddsimca': {title: 'DD-SIMCA', url: 'https://mda.tools/ddsimca/'},
   'ddsimca-love': {title: 'DD-SIMCA with LOVE', url: 'https://mda.tools/ddsimca-love/'},
};

function appInfo(app) {
   // an own key only: 'constructor' or 'toString' would pass a plain lookup
   if (!Object.hasOwn(APPS, app)) throw new Error('Unknown app: ' + app + '.');
   return APPS[app];
}


/**
 * Download CSV file with DD-SIMCA results.
 *
 * @param {Object} model - JSON with PCA model.
 * @param {Object} simca - JSON with DD-SIMCA model.
 * @param {Object} res - JSON with results object.
 * @param {Object} dataset - JSON with original dataset.
 * @param {string} name - name of the results ('cal', 'test', 'pv', 'new')
 * @param {string} app - id of the app ('ddsimca' or 'ddsimca-love')
 */
export function downloadResults(model, simca, res, dataset, name, app = 'ddsimca') {

   const {title, url: link} = appInfo(app);

   // the excluded objects are taken from the result and not from the caller. the result was
   // computed with one particular list and every value below is read with a stride derived from
   // it, so a list handed in on the side can disagree with the data it is applied to. it did: the
   // pv set is built from the outlier free calibration result, and passing the calibration
   // outliers alongside it subtracted them a second time - most rows of the file then carried one
   // object's label next to another object's distances
   // an entry that names no real row is silent: it matches no whole 'i' below, so a row the
   // result has no values for is emitted and the file is written misaligned. see
   // 'checkedResultOutliers' for what is refused and why none of it is caught further down
   const outliers = checkedResultOutliers(res.pca.outliers, dataset.values.nrows);

   let exclvars = model.exclvars;
   if (!exclvars || !Array.isArray(exclvars)) {
      exclvars = [];
   }

   // 'n' is the number of rows the result actually holds and 'nAll' the number in the dataset it
   // was computed from. if the two disagree the file would come out silently misaligned, which is
   // worse than not producing it at all
   const nAll = dataset.values.nrows;
   const nOut = outliers.length;
   const n = res.pca.nrows;

   if (nAll - nOut !== n) {
      throw new Error(
         'Results do not match the dataset: ' + nAll + ' rows minus ' + nOut +
         ' excluded is not ' + n + '.'
      );
   }

   const ncomp = simca.ncomp;
   const q = pcares_get(res.pca, 'qdist', ncomp);
   const h = pcares_get(res.pca, 'hdist', ncomp);

   const lt = simca.limType === 'classic' ? 'c' : 'r';
   const f = pcares_get(res.pca, 'fdist' + lt, ncomp);
   const Nh = pcamodel_get(model, lt + 'Nh')[ncomp - 1];
   const Nq = pcamodel_get(model, lt + 'Nq')[ncomp - 1];
   const h0 = pcamodel_get(model, lt + 'h0')[ncomp - 1];
   const q0 = pcamodel_get(model, lt + 'q0')[ncomp - 1];
   const Nf = Nh + Nq;

   const v = res.simca.v[ncomp - 1];
   const sep = dataset.sep;
   const dec = dataset.dec;

   // every number is written with the file's decimal separator as the row is built. the old code
   // ran one replace over the finished document instead, which rewrote every dot in it: an object
   // label 'sample.1' was exported as 'sample,1', while the title, the target class and the file
   // name were added after the replace and kept theirs, so one name could be spelled two ways in
   // the same file. only a value which is a number, or a string which is nothing but a number,
   // is converted here - text keeps whatever it contains.
   //
   // a stranger distribution which could not be fitted leaves 'beta', 's' and 'f0' at NaN, and
   // 'selectivity' is 1 - beta so it goes with them. the javascript spelling of a non-finite
   // number is not something a spreadsheet reads as a value, so they are written as text
   const num2str = v => {
      if (typeof v === 'number') {
         if (Number.isNaN(v)) return '-';
         if (v === Infinity) return 'Inf';
         if (v === -Infinity) return '-Inf';
         return String(v).replace('.', dec);
      }
      return typeof v === 'string' && /^-?\d+\.\d+$/.test(v) ? v.replace('.', dec) : v;
   };

   const row = (values, end = '\n') => values.map(num2str).join(sep) + end;

   const hasClasses = res.simca.classes && res.simca.classes.length === n;
   // the metadata rows are padded out to the width of the object table below them, which is eight
   // columns wide whether or not the set carries classes
   const filler = ['', '', '', '', '', ''];
   let out = '';
   out += row(['Reference classes:',  hasClasses ? 'provided' : 'not provided', ...filler]);
   out += row(['Number of components:', ncomp, ...filler]);
   out += row(['Alpha:', (simca.alpha/100).toFixed(3), ...filler]);
   out += row(['Limits estimator:', simca.limType, ...filler]);
   out += '\n';

   out += row(['Nh:', Nh, ...filler]);
   out += row(['Nq:', Nq, ...filler]);
   out += row(['Nf:', Nf, ...filler]);
   out += '\n';

   out += row(['F crit (extremes):', simca.eCrit[ncomp - 1], ...filler]);
   out += row(['F crit (outliers):', simca.oCrit[ncomp - 1], ...filler]);
   out += '\n';

   if (exclvars.length > 0) {
      out += row(['Number of excluded variables:', exclvars.length, ...filler]);
      // the model's list is 0-based; the file is read by people, who number variables from 1
      out += row(['Indices of excluded variables:', convertToIntervals(exclvars.map(i => i + 1)),
         ...filler]);
      out += '\n';
   }

   // overall statistics
   out += row(['Objects:', nAll, ...filler]);
   out += row(['In:', v.decsNum.in, ...filler]);
   out += row(['Out:', v.decsNum.out, ...filler]);
   out += row(['Excluded:', nOut, ...filler]);

   // statistics for members if any
   if (res.simca.nMembers > 0) {
      out += '\n';
      out += row(['Members:', res.simca.nMembers, ...filler]);
      out += row(['Regular:', v.rolsNum.regular, ...filler]);
      out += row(['Extreme:', v.rolsNum.extreme, ...filler]);
      out += row(['Outliers:', v.rolsNum.outlier, ...filler]);
      out += row(['Sensitivity:', res.simca.stat.sensitivity[ncomp - 1], ...filler]);
   }

   // statistics for strangers if any
   if (res.simca.nStrangers > 0) {
      out += '\n';
      out += row(['Strangers:', res.simca.nStrangers, ...filler]);
      out += row(['In:', v.decsNum.in - v.rolsNum.regular, ...filler]);
      out += row(['Aliens:', v.rolsNum.alien, ...filler]);
      out += row(['External:', v.rolsNum.external, ...filler]);
      out += row(['Specificity:', res.simca.stat.specificity[ncomp - 1], ...filler]);
      out += row(['Selectivity:', res.simca.stat.selectivity[ncomp - 1], ...filler]);

      out += '\n';
      out += row(['Beta:', v.beta, ...filler]);
      out += row(['Non-centrality (s):', v.s, ...filler]);
      out += row(['Scaling (f0):', v.f0, ...filler]);

      if (res.simca.nMembers > 0) {
         const TPR = res.simca.stat.sensitivity[ncomp - 1];
         const FPR = 1 - res.simca.stat.specificity[ncomp - 1];

         // a ratio with a zero denominator is either undefined (nothing over nothing) or infinite,
         // and writing the javascript spelling of either into the file gives a column which no
         // spreadsheet reads as a number
         const ratio = (num, den) => den !== 0 ? num / den : (num === 0 ? '-' : 'Inf');

         const PPV = ratio(v.TP, v.TP + v.FP);

         // f1 is taken from the counts rather than from precision and recall. the usual form goes
         // undefined as soon as precision is zero over zero, while the count form is defined and
         // equal to zero whenever any member or stranger was classified at all
         const F1 = ratio(2 * v.TP, 2 * v.TP + v.FP + v.FN);
         const PLR = ratio(TPR, FPR);
         const NLR = ratio(1 - TPR, 1 - FPR);
         out += '\n';
         out += row(['Efficiency:', res.simca.stat.efficiency[ncomp - 1], ...filler]);
         out += row(['Accuracy:', res.simca.stat.accuracy[ncomp - 1], ...filler]);
         out += row(['Precision:', PPV, ...filler]);
         out += row(['F1-score:', F1, ...filler]);
         out += row(['PLR:', PLR, ...filler]);
         out += row(['NLR:', NLR, ...filler]);
      }
   }


   // outcomes for every object
   out += '\n';
   out += row(['', 'object', 'class', 'role', 'decision', 'h/h0', 'q/q0', 'f']);


   let j = 0;
   for (let i = 0; i < nAll; i++) {

      if (nOut > 0 && outliers.includes(i)) {
         out += row([
            '',
            dataset.objlabels[i],
            '-',
            'removed',
            '-',
            '-',
            '-',
            '-'
         ]);
         continue;
      }

      out += row([
         '',
         dataset.objlabels[i],
         // a set loaded without a class column has no classes at all, and reading one anyway put
         // the text 'undefined' in every row of the file while the header said 'not provided'.
         // the column stays so that the rows keep the width of the header above them
         hasClasses ? res.pca.classes[j] : '',
         v.rols[j],
         v.decs[j],
         h[j] / h0,
         q[j] / q0,
         f[j]
      ]);
      j = j + 1;
   }
   out += '\n';




   out = row(['Target class:', simca.className, ...filler]) + out;
   out = row(['Data filename:', dataset.fileName, ...filler]) + out;

   let hdr = row([title + ' results (' + name + ')', link, ...filler], '\n\n');

   const blob = new Blob([hdr + out], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `${app}-results-${name}.csv`);
   a.click()
   URL.revokeObjectURL(url)
}


/**
 * Download DDSIMCA model as JSON file.
 *
 * @param {Object} model - JSON with PCA model.
 * @param {Object} simca - JSON with DD-SIMCA model.
 * @param {Object} prepmodel - JSON with preprocessing model.
 * @param {string} app - id of the app ('ddsimca' or 'ddsimca-love')
 */
export function downloadModel(model, simca, prepmodel, app = 'ddsimca') {

   appInfo(app);
   const pca = prepmodel ? {...model, prep: prepmodel} : model;
   const blob = new Blob([JSON.stringify({pca, simca}, replacer)], { type: 'application/json' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', app + '-model.json');
   a.click()
   URL.revokeObjectURL(url)
}
